import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Eye, EyeOff, ShieldAlert, Shield } from "lucide-react";

export default function SettingsPage() {
  const [cats, setCats] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState("");
  const [newCatRestricted, setNewCatRestricted] = useState(false);
  const [newCatColor, setNewCatColor] = useState("#114B5F");
  const [newStatus, setNewStatus] = useState({ name: "", key: "", color: "#64748B", visible_to_gestor: true });

  // Edit states
  const [editCatId, setEditCatId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [editCatColor, setEditCatColor] = useState("");
  const [editStatusId, setEditStatusId] = useState(null);
  const [editStatusName, setEditStatusName] = useState("");
  const [editStatusKey, setEditStatusKey] = useState("");
  const [editStatusColor, setEditStatusColor] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/categories"), api.get("/statuses")]);
        setCats(c.data);
        setStats(s.data);
      } catch (e) { toast.error("Erro ao carregar configurações"); }
      finally { setLoading(false); }
    })();
  }, []);

  // ---- Categories ----
  const addCat = async () => {
    if (!newCat.trim()) return;
    try {
      const r = await api.post("/categories", { name: newCat, restricted: newCatRestricted, color: newCatColor || null });
      setCats((p) => [...p, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCat("");
      setNewCatRestricted(false);
      toast.success("Categoria criada");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const delCat = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta categoria?")) return;
    try {
      await api.delete(`/categories/${id}`);
      setCats((p) => p.filter((x) => x.id !== id));
      toast.success("Categoria excluída");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const startEditCat = (cat) => {
    setEditCatId(cat.id);
    setEditCatName(cat.name);
    setEditCatColor(cat.color || "");
  };

  const saveEditCat = async (id) => {
    try {
      const r = await api.put(`/categories/${id}`, { name: editCatName, color: editCatColor || null });
      setCats((p) => p.map((c) => c.id === id ? r.data : c));
      setEditCatId(null);
      toast.success("Categoria atualizada");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleCatRestricted = async (cat) => {
    try {
      const r = await api.put(`/categories/${cat.id}`, { restricted: !cat.restricted });
      setCats((p) => p.map((c) => c.id === cat.id ? r.data : c));
      toast.success(r.data.restricted ? "Categoria ocultada do gestor" : "Categoria visível para o gestor");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ---- Statuses ----
  const addStatus = async () => {
    if (!newStatus.name || !newStatus.key) return;
    try {
      const r = await api.post("/statuses", newStatus);
      setStats((p) => [...p, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewStatus({ name: "", key: "", color: "#64748B", visible_to_gestor: true });
      toast.success("Status criado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const delStatus = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este status?")) return;
    try {
      await api.delete(`/statuses/${id}`);
      setStats((p) => p.filter((x) => x.id !== id));
      toast.success("Status excluído");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const startEditStatus = (s) => {
    setEditStatusId(s.id);
    setEditStatusName(s.name);
    setEditStatusKey(s.key);
    setEditStatusColor(s.color);
  };

  const saveEditStatus = async (id) => {
    try {
      const r = await api.put(`/statuses/${id}`, { name: editStatusName, key: editStatusKey, color: editStatusColor });
      setStats((p) => p.map((s) => s.id === id ? r.data : s));
      setEditStatusId(null);
      toast.success("Status atualizado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleStatusVisibility = async (s) => {
    try {
      const r = await api.put(`/statuses/${s.id}`, { visible_to_gestor: !s.visible_to_gestor });
      setStats((p) => p.map((st) => st.id === s.id ? r.data : st));
      toast.success(r.data.visible_to_gestor ? "Status visível para o gestor" : "Status ocultado do gestor");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Admin</p>
        <h1 className="font-heading text-3xl font-bold mt-1">Configurações</h1>
      </header>

      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories" data-testid="tab-categories">Categorias</TabsTrigger>
          <TabsTrigger value="statuses" data-testid="tab-statuses">Status</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="mt-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Input placeholder="Nome da nova categoria" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-cat-input" />
                </div>
                <div>
                  <Input type="color" value={newCatColor} onChange={(e) => setNewCatColor(e.target.value)} className="h-10 w-12 p-1" title="Cor da categoria" />
                </div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer whitespace-nowrap select-none px-2 py-2 rounded border border-input hover:bg-accent transition-colors">
                  <input type="checkbox" checked={newCatRestricted} onChange={(e) => setNewCatRestricted(e.target.checked)} className="accent-primary" />
                  <ShieldAlert size={13} /> Restrita
                </label>
                <Button onClick={addCat} className="gap-2 shrink-0" data-testid="add-cat-btn"><Plus size={14} /> Adicionar</Button>
              </div>

              {loading ? <Skeleton className="h-40" /> : (
                <ul className="divide-y">
                  {cats.map((c) => (
                    <li key={c.id} className="flex items-center justify-between py-2.5 group" data-testid={`cat-row-${c.id}`}>
                      {editCatId === c.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input type="color" value={editCatColor || "#114B5F"} onChange={(e) => setEditCatColor(e.target.value)} className="h-8 w-10 p-0.5" />
                          <Input value={editCatName} onChange={(e) => setEditCatName(e.target.value)} className="h-8 max-w-xs" autoFocus />
                          <Button variant="ghost" size="sm" onClick={() => saveEditCat(c.id)} className="text-green-600 hover:text-green-700 h-7 w-7 p-0"><Check size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditCatId(null)} className="text-muted-foreground h-7 w-7 p-0"><X size={14} /></Button>
                        </div>
                      ) : (
                        <span className="text-sm flex items-center gap-2">
                          {c.color && <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />}
                          {c.name}
                          {c.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">padrão</span>}
                          {c.restricted && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-0.5"><ShieldAlert size={10} /> restrita</span>}
                        </span>
                      )}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" onClick={() => toggleCatRestricted(c)} className={`h-7 w-7 p-0 ${c.restricted ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground hover:text-foreground"}`} title={c.restricted ? "Tornar visível para gestor" : "Ocultar do gestor"}>
                          {c.restricted ? <EyeOff size={14} /> : <Eye size={14} />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => startEditCat(c)} className="text-muted-foreground hover:text-foreground h-7 w-7 p-0" data-testid={`edit-cat-${c.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => delCat(c.id)} className="text-brand-red hover:text-brand-red h-7 w-7 p-0" data-testid={`del-cat-${c.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statuses" className="mt-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-5 gap-2 items-end">
                <div>
                  <Label className="text-xs">Chave</Label>
                  <Input value={newStatus.key} onChange={(e) => setNewStatus({ ...newStatus, key: e.target.value.toLowerCase().replace(/\s/g, "_") })} placeholder="ex: em_revisao" data-testid="new-status-key" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Nome exibido</Label>
                  <Input value={newStatus.name} onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })} placeholder="Em revisão" data-testid="new-status-name" />
                </div>
                <div>
                  <Label className="text-xs">Cor</Label>
                  <Input type="color" value={newStatus.color} onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })} className="h-10 p-1" data-testid="new-status-color" />
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none mb-1">
                    <input type="checkbox" checked={newStatus.visible_to_gestor} onChange={(e) => setNewStatus({ ...newStatus, visible_to_gestor: e.target.checked })} className="accent-primary" />
                    <Eye size={12} /> Gestor
                  </label>
                </div>
                <div className="col-span-5">
                  <Button onClick={addStatus} className="gap-2" data-testid="add-status-btn"><Plus size={14} /> Adicionar status</Button>
                </div>
              </div>

              {loading ? <Skeleton className="h-40" /> : (
                <ul className="divide-y">
                  {stats.map((s) => (
                    <li key={s.id} className="flex items-center justify-between py-2.5 group" data-testid={`status-row-${s.id}`}>
                      {editStatusId === s.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input value={editStatusName} onChange={(e) => setEditStatusName(e.target.value)} className="h-8 max-w-[180px]" placeholder="Nome" autoFocus />
                          <Input value={editStatusKey} onChange={(e) => setEditStatusKey(e.target.value)} className="h-8 max-w-[120px] font-mono text-xs" placeholder="chave" />
                          <Input type="color" value={editStatusColor} onChange={(e) => setEditStatusColor(e.target.value)} className="h-8 w-10 p-0.5" />
                          <Button variant="ghost" size="sm" onClick={() => saveEditStatus(s.id)} className="text-green-600 hover:text-green-700 h-7 w-7 p-0"><Check size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditStatusId(null)} className="text-muted-foreground h-7 w-7 p-0"><X size={14} /></Button>
                        </div>
                      ) : (
                        <span className="text-sm flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                          {s.name} <span className="text-xs font-mono text-muted-foreground">({s.key})</span>
                          {!s.visible_to_gestor && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-0.5"><EyeOff size={10} /> oculto</span>}
                        </span>
                      )}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" onClick={() => toggleStatusVisibility(s)} className={`h-7 w-7 p-0 ${!s.visible_to_gestor ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground hover:text-foreground"}`} title={s.visible_to_gestor ? "Ocultar do gestor" : "Tornar visível para gestor"}>
                          {s.visible_to_gestor ? <Eye size={14} /> : <EyeOff size={14} />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => startEditStatus(s)} className="text-muted-foreground hover:text-foreground h-7 w-7 p-0" data-testid={`edit-status-${s.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => delStatus(s.id)} className="text-brand-red hover:text-brand-red h-7 w-7 p-0" data-testid={`del-status-${s.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
